import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, doc, addDoc, deleteDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import Swal from 'sweetalert2';
import { Save, Trash2, Edit3, Briefcase, Eye, Download, Info, CheckCircle2, ListFilter, Users, PlusCircle, X, Globe } from 'lucide-react';
import Translate from '../components/Translate';

const AdminCareers = () => {
  const [activeTab, setActiveTab] = useState('jobs'); // 'jobs' or 'applications'
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  
  // Job Form fields
  const [jobFormLang, setJobFormLang] = useState('en'); // 'en' | 'mr'
  const [editingJobId, setEditingJobId] = useState(null);
  const [title, setTitle] = useState('');
  const [titleMr, setTitleMr] = useState('');
  const [dept, setDept] = useState('Production');
  const [deptMr, setDeptMr] = useState('');
  const [type, setType] = useState('Full-Time');
  const [typeMr, setTypeMr] = useState('');
  const [location, setLocation] = useState('Factory Site');
  const [locationMr, setLocationMr] = useState('');
  const [skills, setSkills] = useState('');
  const [skillsMr, setSkillsMr] = useState('');
  const [qualification, setQualification] = useState('');
  const [qualificationMr, setQualificationMr] = useState('');
  const [vacancies, setVacancies] = useState('1');
  const [submittingJob, setSubmittingJob] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    // Listen to jobs
    const unsubJobs = onSnapshot(collection(db, 'jobs'), (snapshot) => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setJobs(items);
    });

    // Listen to applications
    const unsubApps = onSnapshot(collection(db, 'applications'), (snapshot) => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setApplications(items);
      setLoading(false);
    });

    return () => {
      unsubJobs();
      unsubApps();
    };
  }, []);

  const handleEditClick = (job) => {
    setEditingJobId(job.id);
    setTitle(job.title || '');
    setTitleMr(job.titleMr || '');
    setDept(job.dept || '');
    setDeptMr(job.deptMr || '');
    setType(job.type || 'Full-Time');
    setTypeMr(job.typeMr || '');
    setLocation(job.location || 'Factory Site');
    setLocationMr(job.locationMr || '');
    setSkills(job.skills || '');
    setSkillsMr(job.skillsMr || '');
    setQualification(job.qualification || '');
    setQualificationMr(job.qualificationMr || '');
    setVacancies(job.vacancies || '1');
  };

  const handleCancelEdit = () => {
    setEditingJobId(null);
    setTitle('');
    setTitleMr('');
    setDept('Production');
    setDeptMr('');
    setType('Full-Time');
    setTypeMr('');
    setLocation('Factory Site');
    setLocationMr('');
    setSkills('');
    setSkillsMr('');
    setQualification('');
    setQualificationMr('');
    setVacancies('1');
  };

  const handlePostJob = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      Swal.fire('Error', 'Please fill in job title.', 'error');
      return;
    }

    setSubmittingJob(true);
    try {
      const jobData = {
        title: title.trim(),
        titleMr: titleMr.trim(),
        dept: dept.trim(),
        deptMr: deptMr.trim(),
        type,
        typeMr: typeMr.trim(),
        location,
        locationMr: locationMr.trim(),
        skills: skills.trim(),
        skillsMr: skillsMr.trim(),
        qualification: qualification.trim(),
        qualificationMr: qualificationMr.trim(),
        vacancies: vacancies.toString().trim() || '1'
      };

      if (editingJobId) {
        await updateDoc(doc(db, 'jobs', editingJobId), {
          ...jobData,
          updatedAt: serverTimestamp()
        });

        Swal.fire({
          title: 'Job Updated!',
          text: 'The job posting details have been saved.',
          icon: 'success',
          confirmButtonColor: '#2E7D32'
        });
        setEditingJobId(null);
      } else {
        await addDoc(collection(db, 'jobs'), {
          ...jobData,
          createdAt: serverTimestamp()
        });

        Swal.fire({
          title: 'Job Posted!',
          text: 'The new job listing is now live.',
          icon: 'success',
          confirmButtonColor: '#2E7D32'
        });
      }

      // Clear fields
      setTitle('');
      setTitleMr('');
      setSkills('');
      setSkillsMr('');
      setQualification('');
      setQualificationMr('');
      setVacancies('1');
    } catch (error) {
      console.error('Error saving job:', error);
      Swal.fire('Error', 'Could not save the job posting. Please try again.', 'error');
    } finally {
      setSubmittingJob(false);
    }
  };

  const handleDeleteJob = (id, jobTitle) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete the job posting for "${jobTitle}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#2E7D32',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteDoc(doc(db, 'jobs', id));
          Swal.fire('Deleted!', 'Job posting has been removed.', 'success');
        } catch (error) {
          console.error('Error deleting job:', error);
          Swal.fire('Error', 'Could not delete the job.', 'error');
        }
      }
    });
  };

  const handleMarkReviewed = async (id) => {
    try {
      await updateDoc(doc(db, 'applications', id), {
        status: 'Reviewed'
      });
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Application marked as reviewed',
        showConfirmButton: false,
        timer: 1500
      });
    } catch (error) {
      console.error('Error updating application:', error);
      Swal.fire('Error', 'Could not update status.', 'error');
    }
  };

  const handleDeleteApp = (id, applicantName) => {
    Swal.fire({
      title: 'Delete Application?',
      text: `Are you sure you want to delete the application from "${applicantName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#2E7D32',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteDoc(doc(db, 'applications', id));
          Swal.fire('Deleted!', 'Application has been deleted.', 'success');
        } catch (error) {
          console.error('Error deleting application:', error);
          Swal.fire('Error', 'Could not delete the application.', 'error');
        }
      }
    });
  };

  const handleViewResume = (base64Data, applicantName) => {
    const newTab = window.open();
    if (newTab) {
      newTab.document.write(`<iframe src="${base64Data}" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>`);
    } else {
      Swal.fire('Popup Blocked', 'Please allow popups to view this resume.', 'warning');
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto space-y-10 font-body">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div>
          <h1 className="text-3xl font-heading font-extrabold text-charcoal">
            <Translate>Careers Manager</Translate>
          </h1>
          <p className="text-slate text-sm mt-1">
            <Translate>Manage open job descriptions and inspect candidate applications.</Translate>
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex bg-white p-1 rounded-xl border border-gray-200 shadow-premium w-fit">
          <button
            onClick={() => setActiveTab('jobs')}
            className={`px-6 py-2.5 rounded-lg text-xs font-heading font-extrabold uppercase tracking-wider transition-all duration-300 flex items-center gap-2 cursor-pointer ${
              activeTab === 'jobs'
                ? 'bg-emerald text-white shadow-emerald'
                : 'text-slate hover:text-emerald'
            }`}
          >
            <Briefcase size={14} />
            <span><Translate>Jobs Manager</Translate></span>
          </button>
          <button
            onClick={() => setActiveTab('applications')}
            className={`px-6 py-2.5 rounded-lg text-xs font-heading font-extrabold uppercase tracking-wider transition-all duration-300 flex items-center gap-2 cursor-pointer ${
              activeTab === 'applications'
                ? 'bg-emerald text-white shadow-emerald'
                : 'text-slate hover:text-emerald'
            }`}
          >
            <Users size={14} />
            <span><Translate>Applications</Translate></span>
            {applications.filter(a => a.status !== 'Reviewed').length > 0 && (
              <span className="bg-red-500 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold font-heading">
                {applications.filter(a => a.status !== 'Reviewed').length}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="gold-divider"></div>

      {activeTab === 'jobs' ? (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Post Job Form */}
          <form onSubmit={handlePostJob} className="lg:col-span-5 bg-white p-8 rounded-2xl border border-gray-150 shadow-premium space-y-5">
            <h2 className="text-lg font-heading font-bold text-charcoal flex items-center justify-between">
              <span className="flex items-center gap-2">
                <PlusCircle className="text-emerald" size={20} />
                <span><Translate>{editingJobId ? 'Edit Job Opening' : 'Post a New Job'}</Translate></span>
              </span>
            </h2>

            {/* Language Switcher Bar */}
            <div className="flex items-center justify-between bg-emerald/5 p-3 rounded-xl border border-emerald/10">
              <span className="text-[11px] font-bold text-emerald uppercase tracking-wider flex items-center gap-1">
                <Globe size={14} /> {jobFormLang === 'en' ? 'English' : 'मराठी'}
              </span>
              <div className="flex bg-white p-0.5 rounded-lg border border-gray-200 shadow-sm gap-1">
                <button
                  type="button"
                  onClick={() => setJobFormLang('en')}
                  className={`px-3 py-1 rounded-md text-[11px] font-bold transition-all ${
                    jobFormLang === 'en' ? 'bg-sugarcane-green text-white shadow-sm' : 'text-gray-500'
                  }`}
                >
                  English 🇬🇧
                </button>
                <button
                  type="button"
                  onClick={() => setJobFormLang('mr')}
                  className={`px-3 py-1 rounded-md text-[11px] font-bold transition-all ${
                    jobFormLang === 'mr' ? 'bg-sugarcane-green text-white shadow-sm' : 'text-gray-500'
                  }`}
                >
                  मराठी 🇮🇳
                </button>
              </div>
            </div>
            <div className="gold-divider"></div>

            {jobFormLang === 'en' ? (
              <>
                <div className="space-y-2">
                  <label className="text-xs font-heading font-bold text-slate uppercase tracking-wider"><Translate>Job Title (English)</Translate></label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Senior Chemical Engineer"
                    className="w-full bg-ivory border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-heading font-bold text-slate uppercase tracking-wider"><Translate>Department (English)</Translate></label>
                    <input
                      type="text"
                      value={dept}
                      onChange={(e) => setDept(e.target.value)}
                      placeholder="e.g. Production / Agriculture"
                      className="w-full bg-ivory border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-heading font-bold text-slate uppercase tracking-wider"><Translate>Job Type</Translate></label>
                    <select
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                      className="w-full bg-ivory border border-gray-200 rounded-xl px-3 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
                    >
                      <option value="Full-Time">Full-Time</option>
                      <option value="Part-Time">Part-Time</option>
                      <option value="Contract">Contract</option>
                      <option value="Remote/Hybrid">Remote/Hybrid</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-heading font-bold text-slate uppercase tracking-wider"><Translate>Location (English)</Translate></label>
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g. Factory Site / Pune HQ"
                    className="w-full bg-ivory border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-heading font-bold text-slate uppercase tracking-wider"><Translate>Skills Required (English)</Translate></label>
                  <input
                    type="text"
                    value={skills}
                    onChange={(e) => setSkills(e.target.value)}
                    placeholder="e.g. Process Control, Safety, Distillation"
                    className="w-full bg-ivory border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
                    required
                  />
                  <p className="text-[10px] text-slate/50">Enter skills as a comma-separated list.</p>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-heading font-bold text-slate uppercase tracking-wider"><Translate>Minimum Qualification (English)</Translate></label>
                  <input
                    type="text"
                    value={qualification}
                    onChange={(e) => setQualification(e.target.value)}
                    placeholder="e.g. B.Tech in Chemical Engineering"
                    className="w-full bg-ivory border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
                    required
                  />
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <label className="text-xs font-heading font-bold text-slate uppercase tracking-wider"><Translate>Job Title (मराठी)</Translate></label>
                  <input
                    type="text"
                    value={titleMr}
                    onChange={(e) => setTitleMr(e.target.value)}
                    placeholder="उदा. वरिष्ठ केमिकल इंजिनिअर"
                    className="w-full bg-ivory border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-heading font-bold text-slate uppercase tracking-wider"><Translate>Department (मराठी)</Translate></label>
                    <input
                      type="text"
                      value={deptMr}
                      onChange={(e) => setDeptMr(e.target.value)}
                      placeholder="उदा. उत्पादन / शेती विभाग"
                      className="w-full bg-ivory border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-heading font-bold text-slate uppercase tracking-wider"><Translate>Job Type (मराठी)</Translate></label>
                    <input
                      type="text"
                      value={typeMr}
                      onChange={(e) => setTypeMr(e.target.value)}
                      placeholder="उदा. पूर्णवेळ / कंत्राटी"
                      className="w-full bg-ivory border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-heading font-bold text-slate uppercase tracking-wider"><Translate>Location (मराठी)</Translate></label>
                  <input
                    type="text"
                    value={locationMr}
                    onChange={(e) => setLocationMr(e.target.value)}
                    placeholder="उदा. कारखाना स्थळ"
                    className="w-full bg-ivory border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-heading font-bold text-slate uppercase tracking-wider"><Translate>Skills Required (मराठी)</Translate></label>
                  <input
                    type="text"
                    value={skillsMr}
                    onChange={(e) => setSkillsMr(e.target.value)}
                    placeholder="उदा. डिस्टिलेशन, गुणवत्ता नियंत्रण, सुरक्षा"
                    className="w-full bg-ivory border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-heading font-bold text-slate uppercase tracking-wider"><Translate>Minimum Qualification (मराठी)</Translate></label>
                  <input
                    type="text"
                    value={qualificationMr}
                    onChange={(e) => setQualificationMr(e.target.value)}
                    placeholder="उदा. बी.टेक / पदवीधर"
                    className="w-full bg-ivory border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <label className="text-xs font-heading font-bold text-slate uppercase tracking-wider"><Translate>Number of Vacancies</Translate></label>
              <input
                type="number"
                min="1"
                value={vacancies}
                onChange={(e) => setVacancies(e.target.value)}
                placeholder="e.g. 5"
                className="w-full bg-ivory border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
                required
              />
            </div>

            <div className="flex gap-3">
              {editingJobId && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-4 py-3.5 bg-gray-100 hover:bg-gray-200 text-charcoal rounded-xl text-xs font-heading font-bold transition-colors cursor-pointer flex items-center gap-1.5"
                >
                  <X size={14} />
                  <span>Cancel</span>
                </button>
              )}
              <button
                type="submit"
                disabled={submittingJob}
                className="flex-1 btn-premium flex items-center justify-center gap-2 py-4 cursor-pointer"
              >
                <Save size={16} />
                <span>
                  {submittingJob 
                    ? (editingJobId ? <Translate>Updating...</Translate> : <Translate>Posting...</Translate>) 
                    : (editingJobId ? <Translate>Update Opening</Translate> : <Translate>Post Opening</Translate>)}
                </span>
              </button>
            </div>
          </form>

          {/* Jobs List */}
          <div className="lg:col-span-8 bg-white p-8 rounded-2xl border border-gray-150 shadow-premium space-y-5">
            <h2 className="text-lg font-heading font-bold text-charcoal flex items-center gap-2">
              <ListFilter className="text-emerald" size={20} />
              <span><Translate>Active Job Openings</Translate></span>
            </h2>
            <div className="gold-divider"></div>

            {loading ? (
              <div className="text-center py-10 text-slate"><Translate>Loading positions...</Translate></div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-10 text-slate flex flex-col items-center gap-2">
                <Info size={24} className="text-slate/40" />
                <p><Translate>No custom job openings posted yet.</Translate></p>
                <p className="text-xs text-slate/50"><Translate>Default job roles will be shown on the user side.</Translate></p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-gray-100 text-xs text-slate uppercase tracking-wider font-heading font-bold">
                      <th className="pb-3"><Translate>Title</Translate></th>
                      <th className="pb-3"><Translate>Department</Translate></th>
                      <th className="pb-3"><Translate>Type / Location</Translate></th>
                      <th className="pb-3"><Translate>Vacancies</Translate></th>
                      <th className="pb-3 text-right"><Translate>Action</Translate></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 text-sm">
                    {jobs.map((job) => (
                      <tr key={job.id} className="hover:bg-ivory/20 transition-colors">
                        <td className="py-4">
                          <p className="font-heading font-bold text-charcoal">{job.title}</p>
                          <p className="text-[10px] text-slate/60 mt-0.5 font-semibold">Q: {job.qualification}</p>
                        </td>
                        <td className="py-4 text-xs font-semibold text-emerald">
                          {job.dept}
                        </td>
                        <td className="py-4">
                          <p className="text-xs font-bold text-charcoal">{job.type}</p>
                          <p className="text-[10px] text-slate/50 font-semibold">{job.location}</p>
                        </td>
                        <td className="py-4">
                          <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold px-2.5 py-1 rounded-full">
                            {job.vacancies || 1}
                          </span>
                        </td>
                        <td className="py-4 text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleEditClick(job)}
                              className="p-2 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white rounded-lg transition-colors cursor-pointer"
                              title="Edit Job"
                            >
                              <Edit3 size={14} />
                            </button>
                            <button
                              onClick={() => handleDeleteJob(job.id, job.title)}
                              className="p-2 bg-red-50 text-red-600 hover:bg-red-600 hover:text-white rounded-lg transition-colors cursor-pointer"
                              title="Delete Job"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Applications List */
        <div className="bg-white p-8 rounded-2xl border border-gray-150 shadow-premium space-y-6">
          <h2 className="text-lg font-heading font-bold text-charcoal flex items-center gap-2">
            <Users className="text-emerald" size={20} />
            <span><Translate>Candidate Applications</Translate></span>
          </h2>
          <div className="gold-divider"></div>

          {loading ? (
            <div className="text-center py-10 text-slate"><Translate>Loading applications...</Translate></div>
          ) : applications.length === 0 ? (
            <div className="text-center py-16 text-slate flex flex-col items-center gap-2">
              <Info size={24} className="text-slate/40" />
              <p className="font-bold text-charcoal"><Translate>No applications received yet.</Translate></p>
              <p className="text-xs text-slate/50"><Translate>Submitted applications from the careers page will display here.</Translate></p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[800px]">
                <thead>
                  <tr className="border-b border-gray-100 text-xs text-slate uppercase tracking-wider font-heading font-bold">
                    <th className="pb-3"><Translate>Applicant</Translate></th>
                    <th className="pb-3"><Translate>Applied Position</Translate></th>
                    <th className="pb-3"><Translate>Experience</Translate></th>
                    <th className="pb-3"><Translate>Applied Date</Translate></th>
                    <th className="pb-3"><Translate>Status</Translate></th>
                    <th className="pb-3 text-right"><Translate>Actions</Translate></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 text-sm">
                  {applications.map((app) => (
                    <tr key={app.id} className="hover:bg-ivory/20 transition-colors">
                      <td className="py-4">
                        <p className="font-heading font-bold text-charcoal">{app.name}</p>
                        <p className="text-[10px] text-slate/60 font-semibold">{app.email}</p>
                        <p className="text-[10px] text-slate/60 font-semibold">{app.contact}</p>
                      </td>
                      <td className="py-4">
                        <span className="bg-sage/10 text-sage-dark px-2.5 py-1 rounded-full text-xs font-bold">
                          {app.role}
                        </span>
                      </td>
                      <td className="py-4 text-xs font-semibold text-charcoal">
                        {app.experience}
                      </td>
                      <td className="py-4 text-slate text-xs">
                        {app.createdAt ? new Date(app.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="py-4">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          app.status === 'Reviewed' 
                            ? 'bg-emerald/10 text-emerald' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {app.status || 'Pending'}
                        </span>
                      </td>
                      <td className="py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleViewResume(app.resume, app.name)}
                            className="p-2 bg-mist text-slate hover:bg-emerald hover:text-white rounded-lg transition-colors cursor-pointer"
                            title="View Resume"
                          >
                            <Eye size={14} />
                          </button>
                          <a
                            href={app.resume}
                            download={`${app.name}_Resume.pdf`}
                            className="p-2 bg-mist text-slate hover:bg-emerald hover:text-white rounded-lg transition-colors flex items-center justify-center cursor-pointer"
                            title="Download Resume"
                          >
                            <Download size={14} />
                          </a>
                          {app.status !== 'Reviewed' && (
                            <button
                              onClick={() => handleMarkReviewed(app.id)}
                              className="p-2 bg-emerald/10 text-emerald hover:bg-emerald hover:text-white rounded-lg transition-colors cursor-pointer"
                              title="Mark Reviewed"
                            >
                              <CheckCircle2 size={14} />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteApp(app.id, app.name)}
                            className="p-2 bg-red-50 text-red-600 hover:bg-red-600 hover:text-white rounded-lg transition-colors cursor-pointer"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminCareers;
